import { z } from "zod";

/**
 * The single Postgres `LISTEN` channel every server pod subscribes to for
 * application-emitted events. DB-trigger channels are translated into the
 * same `RealtimeEvent` shape before fan-out.
 */
export const REALTIME_CHANNEL = "realtime";

/**
 * Postgres `pg_notify` truncates payloads larger than 8000 bytes (NAMEDATALEN
 * minus overhead). We stay well under that and encode payloads as small
 * invalidation hints, not row data.
 */
export const MAX_PAYLOAD_BYTES = 7500;

export type RealtimeTag =
  | "accessions"
  | `accession:${number}`
  | `integration-messages:ITRANSPLANT:${number}`
  | "qc"
  | `qc:${number}`;

export const rt = {
  accessions(): RealtimeTag {
    return "accessions";
  },
  accession(id: number): RealtimeTag {
    return `accession:${id}`;
  },
  iTransplantMessages(accessionId: number): RealtimeTag {
    return `integration-messages:ITRANSPLANT:${accessionId}`;
  },
  /** All QC capture/review queries (dashboard spans every instrument). */
  qc(): RealtimeTag {
    return "qc";
  },
  /** QC queries scoped to a single instrument's run-review series. */
  qcInstrument(instrumentId: number): RealtimeTag {
    return `qc:${instrumentId}`;
  },
} as const;

export function isRealtimeTag(tag: string): tag is RealtimeTag {
  return (
    tag === "accessions" ||
    /^accession:\d+$/.test(tag) ||
    /^integration-messages:ITRANSPLANT:\d+$/.test(tag) ||
    tag === "qc" ||
    /^qc:\d+$/.test(tag)
  );
}

export function normalizeRealtimeTags(
  tags: readonly RealtimeTag[],
): RealtimeTag[] {
  return Array.from(new Set(tags)).sort();
}

export const realtimeTagSchema = z
  .string()
  .refine(isRealtimeTag, "Expected a known realtime invalidation tag");

/**
 * Wire format for an invalidation event flowing from a publisher (worker or
 * server) to all server pods (via Postgres NOTIFY) and on to every connected
 * SSE client.
 */
export const realtimeEventSchema = z.object({
  tags: z.array(realtimeTagSchema).min(1),
  at: z.string(),
});

export type RealtimeEvent = z.infer<typeof realtimeEventSchema>;
